import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Heart, User, LogOut, LayoutDashboard, Search, Menu, X, ChevronDown, Car, Hash, MapPin, Star } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { BsArrowUpRightSquare } from "react-icons/bs";
import SearchableCitySelect from '../components/Searchablecityselect';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = ({ selectedCity, setSelectedCity, cities, showSearch = true }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [vehicleType, setVehicleType] = useState('');
  const [evSubtype, setEvSubtype] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [vehicleSegments, setVehicleSegments] = useState([]);
  const searchRef = useRef(null);

  useEffect(() => {
    if (vehicleType && searchType === 'segment') {
      fetchVehicleSegments(vehicleType);
    } else {
      setVehicleSegments([]);
    }
  }, [vehicleType, searchType]);

  const fetchVehicleSegments = async (type) => {
    try {
      const res = await axios.get(`${API}/vehicle-segments?vehicle_type=${type}`);
      setVehicleSegments(res.data.segments || []);
    } catch (error) {
      console.error('Failed to fetch segments:', error);
    }
  };

  const hideSearchPages = ['/upload-vehicle', '/admin', '/superadmin', '/dashboard', '/analytics', '/agent/bulk-upload', '/agent/dashboard'];
  const shouldShowSearch = showSearch && !hideSearchPages.some(page => location.pathname.startsWith(page));

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const vehicleTypes = [
    { value: '', label: 'Vehicle Type' },
    { value: 'car', label: 'Cars' },
    { value: 'bike', label: 'Bikes' },
    { value: 'ev', label: 'Electric' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const evSubtypes = [
    { value: '', label: 'EV Type' },
    { value: 'car', label: 'EV Car' },
    { value: 'bike', label: 'EV Bike' },
    { value: 'commercial', label: 'EV Commercial' }
  ];

  const searchTypes = [
    { value: 'name', label: 'Vehicle Name', icon: Search },
    { value: 'segment', label: 'Vehicle Segment', icon: Car },
    { value: 'registration', label: 'Vehicle Reg. No.', icon: Hash },
    { value: 'choice_number', label: 'Choice Number', icon: Star },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (vehicleType) params.set('type', vehicleType);
    if (vehicleType === 'ev' && evSubtype) params.set('ev_subtype', evSubtype);
    if (searchQuery.trim()) {
      if (searchType === 'segment') {
        params.set('segment', searchQuery);
      } else if (searchType === 'registration') {
        params.set('reg', searchQuery.toUpperCase().replace(/\s+/g, ''));
      } else if (searchType === 'choice_number') {
          params.set('is_choice_number', 'true');
          if (searchQuery.trim()) {
            // Use registration search so partial matches like "1777" or "KA 22 HV 1777" work
            params.set('reg', searchQuery.toUpperCase().replace(/\s+/g, ''));
          }
        }
      else {
        params.set('q', searchQuery);
      }
    }
    navigate(`/browse?${params.toString()}`);
    setShowSearchDropdown(false);
    setSearchQuery('');
  };

  const words = ["brand", "fuel type", "model"];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isEvMode = vehicleType === 'ev';
  const isSegmentMode = searchType === 'segment' && vehicleType && vehicleSegments.length > 0;

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-secondary-200 shadow-sm">
      <div className="px-3 md:px-4 lg:px-8 xl:px-12 py-3">
        <div className="flex items-center justify-between gap-4">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_vehicle-connect-hub/artifacts/8ccs0uq0_x7%20autoz.png"
              alt="X7 Autoz"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* MOBILE TOP BAR */}
          <div className="md:hidden flex items-center justify-between w-full relative">
            <div className="w-10" />
            {/* Mobile city selector — searchable */}
            <div className="absolute left-1/2 -translate-x-1/2">
              <SearchableCitySelect
                cities={cities || []}
                value={selectedCity}
                onValueChange={setSelectedCity}
                placeholder="City"
                showIcon={true}
                triggerClassName="border-none shadow-none text-sm font-medium px-1 py-1"
                contentWidth="200px"
              />
            </div>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
            </Sheet>
          </div>

          {/* DESKTOP SEARCH BAR */}
          {shouldShowSearch && (
            <div
              ref={searchRef}
              className="hidden md:flex flex-1 min-w-0 md:max-w-sm lg:max-w-xl xl:max-w-2xl"
            >
              <form onSubmit={handleSearch} className="w-full">
                <div className="flex items-center border-2 border-secondary-200 rounded-full overflow-hidden hover:border-primary-400 focus-within:border-primary-500 transition-colors bg-white">

                  {/* Vehicle Type */}
                  <div className="relative border-r border-secondary-200 flex-shrink-0">
                    <select
                      value={vehicleType}
                      onChange={(e) => {
                        setVehicleType(e.target.value);
                        if (e.target.value !== 'ev') setEvSubtype('');
                        setSearchQuery('');
                      }}
                      className="appearance-none bg-transparent pl-4 pr-8 py-2.5 text-sm font-medium text-secondary-700 cursor-pointer focus:outline-none"
                      data-testid="search-vehicle-type"
                    >
                      {vehicleTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-500 pointer-events-none" />
                  </div>

                  {/* EV Subtype */}
                  {isEvMode && (
                    <div className="relative border-r border-secondary-200 flex-shrink-0">
                      <select
                        value={evSubtype}
                        onChange={(e) => setEvSubtype(e.target.value)}
                        className="appearance-none bg-transparent pl-3 pr-7 py-2.5 text-sm font-medium text-secondary-700 cursor-pointer focus:outline-none"
                        data-testid="search-ev-subtype"
                      >
                        {evSubtypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-500 pointer-events-none" />
                    </div>
                  )}

                  {/* Search Type */}
                  <div className="relative border-r border-secondary-200 flex-shrink-0">
                    <select
                      value={searchType}
                      onChange={(e) => {
                        setSearchType(e.target.value);
                        setSearchQuery('');
                      }}
                      className="appearance-none bg-transparent pl-3 pr-7 py-2.5 text-sm font-medium text-secondary-700 cursor-pointer focus:outline-none"
                      data-testid="search-type-selector"
                    >
                      {searchTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-1 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-500 pointer-events-none" />
                  </div>

                  {/* Segment dropdown OR text input */}
                  {isSegmentMode ? (
                    <select
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 min-w-0 px-4 py-2.5 text-sm focus:outline-none bg-transparent"
                      data-testid="segment-search-dropdown"
                    >
                      <option value="">Select Segment...</option>
                      {vehicleSegments.map(segment => (
                        <option key={segment} value={segment}>{segment}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="relative flex-1 min-w-0">
                      {!searchQuery && (
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-sm text-gray-400 flex items-center gap-1 whitespace-nowrap">
                            {searchType === 'choice_number' ? (
                              <span>e.g. 1777, 9999, KA 22 HV 1777...</span>
                            ) : searchType === 'registration' ? (
                              <span>e.g. KA 01 AB 1234...</span>
                            ) : (
                              <>
                                <span>Search for</span>
                                <span key={index} className="transition-all duration-500 opacity-100">
                                  {words[index]}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setShowSearchDropdown(true)}
                        className="w-full px-4 py-2.5 text-sm focus:outline-none bg-transparent"
                      />
                    </div>
                  )}

                  {/* Search Button */}
                  <button
                    type="submit"
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2.5 transition-colors flex-shrink-0"
                    data-testid="search-submit-btn"
                  >
                    <Search className="h-5 w-5 text-black" />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2 lg:gap-4 flex-shrink-0">
            <Link to="/browse" className="text-secondary-700 hover:text-primary-600 font-medium transition-colors" data-testid="nav-buy-link">
              Buy
            </Link>
            <Link to={user ? '/upload-vehicle' : '/login'} className="text-secondary-700 hover:text-primary-600 font-medium transition-colors" data-testid="nav-sell-link">
              Sell
            </Link>
            <Link to="/contact" className="text-secondary-700 hover:text-primary-600 font-medium transition-colors" data-testid="nav-contact-link">
              Contact
            </Link>

            {/* Desktop city selector — searchable */}
            {cities && cities.length > 0 && (
              <SearchableCitySelect
                cities={cities}
                value={selectedCity}
                onValueChange={setSelectedCity}
                placeholder="Select City"
                contentWidth="220px"
                data-testid="city-selector"
              />
            )}
          </nav>

          {/* User Actions - Desktop */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            {user ? (
              <>
                {user.role === 'user' && (
                  <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-primary-300 text-primary-600 hover:bg-primary-50 font-medium">
                    <BsArrowUpRightSquare className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                )}
                {user.role === 'partner' && (
                  <Button variant="outline" onClick={() => navigate('/partner')} className="border-primary-300 text-primary-600 hover:bg-primary-50 font-medium">
                    <BsArrowUpRightSquare className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                )}
                {user.role === 'influencer' && (
                  <Button variant="outline" onClick={() => navigate('/influencer/dashboard')} className="border-primary-300 text-primary-600 hover:bg-primary-50 font-medium">
                    <BsArrowUpRightSquare className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                )}
                {user.role === 'agent' && (
                  <>
                    <Button variant="outline" onClick={() => navigate('/agent/dashboard')} className="border-primary-300 text-primary-600 hover:bg-primary-50 font-medium">
                      <BsArrowUpRightSquare className="h-4 w-4 mr-2" />
                      Agent Dashboard
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-orange-300 text-orange-500 hover:bg-orange-50 font-medium">
                      <BsArrowUpRightSquare className="h-4 w-4 mr-2" />
                      Statistics Dashboard
                    </Button>
                  </>
                )}
                <Button variant="ghost" size="icon" onClick={() => navigate('/wishlist')}>
                  <Heart className="h-10 w-10 text-red-500" fill="currentColor" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(user.role === 'admin' || user.role === 'superadmin') && (
                      <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Dashboard
                      </DropdownMenuItem>
                    )}
                    {(user.role === 'admin' || user.role === 'superadmin') && (
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    {user.role === 'superadmin' && (
                      <DropdownMenuItem onClick={() => navigate('/superadmin')}>
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Super Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')}>Login</Button>
                <Button onClick={() => navigate('/register')} className="bg-orange-400 text-white hover:bg-primary-600">
                  Register
                </Button>
              </>
            )}
          </div>

          {/* Mobile Sheet */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetContent side="right" className="w-[300px]">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-4 mt-6">
                {shouldShowSearch && (
                  <form onSubmit={handleSearch} className="space-y-3">
                    <select
                      value={vehicleType}
                      onChange={(e) => {
                        setVehicleType(e.target.value);
                        if (e.target.value !== 'ev') setEvSubtype('');
                        setSearchQuery('');
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      data-testid="mobile-search-vehicle-type"
                    >
                      {vehicleTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>

                    {/* EV Subtype — mirrors the desktop chain: only shown once "Electric" is picked */}
                    {isEvMode && (
                      <select
                        value={evSubtype}
                        onChange={(e) => setEvSubtype(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                        data-testid="mobile-search-ev-subtype"
                      >
                        {evSubtypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    )}

                    <select
                      value={searchType}
                      onChange={(e) => {
                        setSearchType(e.target.value);
                        setSearchQuery('');
                      }}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      data-testid="mobile-search-type-selector"
                    >
                      {searchTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>

                    <div className="flex gap-2">
                      {isSegmentMode ? (
                        <select
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="flex-1 border rounded-lg px-3 py-2 text-sm"
                          data-testid="mobile-segment-search-dropdown"
                        >
                          <option value="">Select Segment...</option>
                          {vehicleSegments.map(segment => (
                            <option key={segment} value={segment}>{segment}</option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={
                            searchType === 'choice_number' ? 'e.g. 1777, KA 22 HV 1777...' :
                            searchType === 'registration' ? 'e.g. KA 01 AB 1234...' :
                            'Search vehicles...'
                          }
                          className="flex-1"
                        />
                      )}
                      <Button type="submit" size="icon" className="bg-primary-500 text-black flex-shrink-0">
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                )}

                {/* Mobile sheet city selector — searchable, full width */}
                {cities && cities.length > 0 && (
                  <SearchableCitySelect
                    cities={cities}
                    value={selectedCity}
                    onValueChange={setSelectedCity}
                    placeholder="Select City"
                    contentWidth="260px"
                    triggerClassName="w-full"
                    data-testid="mobile-city-selector"
                  />
                )}

                <div className="border-t pt-4 space-y-2">
                  <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/browse'); setMobileMenuOpen(false); }}>
                    Buy Vehicles
                  </Button>
                  <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate(user ? '/upload-vehicle' : '/login'); setMobileMenuOpen(false); }}>
                    Sell Vehicle
                  </Button>
                </div>

                {user ? (
                  <div className="border-t pt-4 space-y-2">
                    <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/wishlist'); setMobileMenuOpen(false); }}>
                      <Heart className="mr-2 h-4 w-4 text-red-500" fill="currentColor" />
                      Wishlist
                    </Button>
                    {user.role === 'user' && (
                      <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>
                        <BsArrowUpRightSquare className="mr-2 h-4 w-4" />
                        Dashboard
                      </Button>
                    )}
                    {user.role === 'agent' && (
                      <>
                        <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/agent/dashboard'); setMobileMenuOpen(false); }}>
                          <BsArrowUpRightSquare className="mr-2 h-4 w-4" />
                          Agent Dashboard
                        </Button>
                        <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>
                          <BsArrowUpRightSquare className="mr-2 h-4 w-4" />
                          Statistics Dashboard
                        </Button>
                      </>
                    )}
                    {user.role === 'partner' && (
                      <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/partner'); setMobileMenuOpen(false); }}>
                        <BsArrowUpRightSquare className="mr-2 h-4 w-4" />
                        Dashboard
                      </Button>
                    )}
                    {user.role === 'influencer' && (
                      <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/influencer/dashboard'); setMobileMenuOpen(false); }}>
                        <BsArrowUpRightSquare className="mr-2 h-4 w-4" />
                        Dashboard
                      </Button>
                    )}
                    {(user.role === 'admin' || user.role === 'superadmin') && (
                      <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}>
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Admin Panel
                      </Button>
                    )}
                    {user.role === 'superadmin' && (
                      <Button variant="ghost" className="w-full justify-start" onClick={() => { navigate('/superadmin'); setMobileMenuOpen(false); }}>
                        <BsArrowUpRightSquare className="mr-2 h-4 w-4" />
                        Super Admin
                      </Button>
                    )}
                    <Button variant="ghost" className="w-full justify-start text-red-600" onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <div className="border-t pt-4 space-y-2">
                    <Button className="w-full" variant="outline" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                      Login
                    </Button>
                    <Button className="w-full bg-orange-400 text-white hover:bg-primary-600" onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>
                      Register
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>

        </div>
      </div>
    </header>
  );
};

export default Header;